import React from "react";

/* ================================================================
   Puzzles & Games — 8 animated templates
   Jigsaw, maze, chess, dice, cards, rubik, board game, word search
   Flat 2D style with playful animations
   ================================================================ */

// ── 1. Jigsaw Puzzle ─────────────────────────────────────────────────
export const JigsawPuzzle: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const colors = ["#6366f1", "#ec4899", "#22c55e", "#f59e0b", "#3b82f6", "#ef4444"];
  // 6 puzzle piece regions
  const pieces = [
    { x: 200, y: 120, w: 130, h: 120, label: "A" },
    { x: 340, y: 120, w: 130, h: 120, label: "B" },
    { x: 480, y: 120, w: 130, h: 120, label: "C" },
    { x: 200, y: 250, w: 130, h: 120, label: "D" },
    { x: 340, y: 250, w: 130, h: 120, label: "E" },
    { x: 480, y: 250, w: 130, h: 120, label: "F" },
  ];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Background board */}
      <rect x="190" y="110" width="430" height="270" rx="8" fill="#1e293b" stroke="#334155" strokeWidth="2" />
      {/* Pieces */}
      {pieces.map((p, i) => {
        const placed = i <= activeIndex;
        const isActive = i === activeIndex;
        const ox = placed ? 0 : (i % 2 === 0 ? -20 : 20);
        const oy = placed ? 0 : -30;
        return (
          <g key={i} opacity={placed ? 1 : 0.5}>
            <rect
              x={p.x + ox}
              y={p.y + oy}
              width={p.w - 4}
              height={p.h - 4}
              rx="5"
              fill={colors[i]}
              stroke={isActive ? "#fff" : "#0f172a"}
              strokeWidth={isActive ? 3 : 1.5}
            >
              {isActive && !placed && (
                <animateTransform attributeName="transform" type="translate" values={`${ox},${oy};0,0;${ox},${oy}`} dur="1.5s" repeatCount="indefinite" />
              )}
            </rect>
            {/* Puzzle notch */}
            <circle cx={p.x + ox + p.w / 2} cy={p.y + oy} r="10" fill={colors[i]} />
            <text x={p.x + ox + p.w / 2 - 2} y={p.y + oy + p.h / 2 + 5} textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold">{p.label}</text>
          </g>
        );
      })}
      <text x="400" y="440" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Piece {activeIndex + 1} / 6</text>
    </svg>
  );
};

// ── 2. Maze Path ─────────────────────────────────────────────────────
export const MazePath: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  const pathLen = 600;
  const dashOffset = pathLen * (1 - Math.min(flowProgress, 1));
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Maze walls */}
      <rect x="100" y="50" width="600" height="400" rx="8" fill="#0f172a" stroke="#6366f1" strokeWidth="2" />
      {/* Walls */}
      <line x1="200" y1="50" x2="200" y2="200" stroke="#475569" strokeWidth="4" />
      <line x1="200" y1="200" x2="350" y2="200" stroke="#475569" strokeWidth="4" />
      <line x1="350" y1="120" x2="350" y2="300" stroke="#475569" strokeWidth="4" />
      <line x1="350" y1="300" x2="500" y2="300" stroke="#475569" strokeWidth="4" />
      <line x1="500" y1="200" x2="500" y2="400" stroke="#475569" strokeWidth="4" />
      <line x1="250" y1="350" x2="450" y2="350" stroke="#475569" strokeWidth="4" />
      <line x1="600" y1="100" x2="600" y2="300" stroke="#475569" strokeWidth="4" />
      <line x1="300" y1="100" x2="500" y2="100" stroke="#475569" strokeWidth="4" />
      {/* Solution path */}
      <path
        d="M130,80 L130,180 L250,180 L250,280 L400,280 L400,180 L550,180 L550,350 L650,350 L650,430"
        fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray={pathLen} strokeDashoffset={dashOffset} strokeLinecap="round"
      />
      {/* Start */}
      <circle cx="130" cy="80" r="10" fill="#22c55e" />
      <text x="130" y="72" textAnchor="middle" fill="#22c55e" fontSize="10">START</text>
      {/* End */}
      <rect x="638" y="420" width="24" height="24" rx="3" fill="#ef4444" />
      <text x="650" y="460" textAnchor="middle" fill="#ef4444" fontSize="10">END</text>
      <text x="400" y="488" textAnchor="middle" fill="#e0e7ff" fontSize="12">Progress: {Math.round(flowProgress * 100)}%</text>
    </svg>
  );
};

// ── 3. Chess Board ───────────────────────────────────────────────────
export const ChessBoard: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const boardSize = 8;
  const cellSize = 40;
  const ox = 200;
  const oy = 60;
  const pieces: Record<string, string> = {
    "0,0": "♜", "1,0": "♞", "2,0": "♝", "3,0": "♛", "4,0": "♚", "5,0": "♝", "6,0": "♞", "7,0": "♜",
    "0,7": "♖", "1,7": "♘", "2,7": "♗", "3,7": "♕", "4,7": "♔", "5,7": "♗", "6,7": "♘", "7,7": "♖",
  };
  for (let i = 0; i < 8; i++) { pieces[`${i},1`] = "♟"; pieces[`${i},6`] = "♙"; }
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x={ox - 5} y={oy - 5} width={boardSize * cellSize + 10} height={boardSize * cellSize + 10} rx="4" fill="#854d0e" />
      {Array.from({ length: boardSize }).map((_, row) =>
        Array.from({ length: boardSize }).map((_, col) => {
          const isDark = (row + col) % 2 === 1;
          const key = `${col},${row}`;
          const piece = pieces[key];
          const cellIdx = row * 8 + col;
          const isHl = cellIdx === activeIndex;
          return (
            <g key={key}>
              <rect x={ox + col * cellSize} y={oy + row * cellSize} width={cellSize} height={cellSize}
                fill={isHl ? "#6366f1" : isDark ? "#92400e" : "#fef3c7"} />
              {piece && (
                <text x={ox + col * cellSize + cellSize / 2} y={oy + row * cellSize + cellSize / 2 + 8} textAnchor="middle"
                  fontSize="22" fill={row < 2 ? "#1e293b" : "#fff"}>
                  {piece}
                </text>
              )}
            </g>
          );
        })
      )}
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Chess</text>
    </svg>
  );
};

// ── 4. Dice Roll ─────────────────────────────────────────────────────
export const DiceRoll: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const face = ((Math.floor(rotation / 60) % 6) + 1);
  const dotPositions: Record<number, Array<[number, number]>> = {
    1: [[200, 200]],
    2: [[160, 160], [240, 240]],
    3: [[160, 160], [200, 200], [240, 240]],
    4: [[160, 160], [240, 160], [160, 240], [240, 240]],
    5: [[160, 160], [240, 160], [200, 200], [160, 240], [240, 240]],
    6: [[160, 160], [240, 160], [160, 200], [240, 200], [160, 240], [240, 240]],
  };
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Dice body */}
      <g transform={`rotate(${rotation % 360}, 200, 200)`}>
        <rect x="120" y="120" width="160" height="160" rx="20" fill="#f8fafc" stroke="#334155" strokeWidth="3">
          <animate attributeName="rx" values="20;25;20" dur="0.8s" repeatCount="indefinite" />
        </rect>
        {/* Dots */}
        {dotPositions[face]?.map(([dx, dy], i) => (
          <circle key={i} cx={dx} cy={dy} r="12" fill="#1e293b" />
        ))}
      </g>
      <text x="200" y="340" textAnchor="middle" fill="#e0e7ff" fontSize="24" fontWeight="bold">{face}</text>
    </svg>
  );
};

// ── 5. Card Deck ─────────────────────────────────────────────────────
export const CardDeck: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const cards = [
    { suit: "♠", value: "A", color: "#1e293b" },
    { suit: "♥", value: "K", color: "#ef4444" },
    { suit: "♦", value: "Q", color: "#ef4444" },
    { suit: "♣", value: "J", color: "#1e293b" },
    { suit: "♥", value: "10", color: "#ef4444" },
  ];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Deck stack */}
      {[4, 3, 2].map((i) => (
        <rect key={`bg-${i}`} x={280 + i * 3} y={80 - i * 3} width="140" height="200" rx="8" fill="#334155" stroke="#475569" strokeWidth="1" />
      ))}
      {/* Dealt cards */}
      {cards.slice(0, activeIndex + 1).map((card, i) => {
        const cx = 100 + i * 130;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            <rect x={cx} y={isActive ? 250 : 260} width="100" height="150" rx="8" fill="#fff" stroke={isActive ? "#6366f1" : "#cbd5e1"} strokeWidth={isActive ? 3 : 1.5}>
              {isActive && <animate attributeName="y" values="280;250;250" dur="0.5s" fill="freeze" />}
            </rect>
            <text x={cx + 12} y={(isActive ? 250 : 260) + 25} fill={card.color} fontSize="16" fontWeight="bold">{card.value}</text>
            <text x={cx + 50} y={(isActive ? 250 : 260) + 90} textAnchor="middle" fill={card.color} fontSize="30">{card.suit}</text>
            <text x={cx + 88} y={(isActive ? 250 : 260) + 140} fill={card.color} fontSize="16" fontWeight="bold" textAnchor="end">{card.value}</text>
          </g>
        );
      })}
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Card {activeIndex + 1}</text>
    </svg>
  );
};

// ── 6. Rubik Cube ────────────────────────────────────────────────────
export const RubikCube: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const faceColors = ["#ef4444", "#3b82f6", "#22c55e", "#fbbf24", "#f97316", "#fff"];
  const cellSize = 35;
  return (
    <svg viewBox="0 0 400 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <g transform={`rotate(${rotation * 0.2}, 200, 200)`}>
        {/* Front face */}
        {[0, 1, 2].map((r) =>
          [0, 1, 2].map((c) => (
            <rect key={`f-${r}-${c}`} x={110 + c * (cellSize + 3)} y={110 + r * (cellSize + 3)} width={cellSize} height={cellSize} rx="3"
              fill={faceColors[(r * 3 + c) % 6]} stroke="#1e293b" strokeWidth="2" />
          ))
        )}
        {/* Top face (parallelogram) */}
        {[0, 1, 2].map((c) => (
          <polygon key={`t-${c}`}
            points={`${110 + c * 38 + 20},${90 - 20} ${110 + c * 38 + 58},${90 - 20} ${110 + c * 38 + 38},${107} ${110 + c * 38},${107}`}
            fill={faceColors[(c + 3) % 6]} stroke="#1e293b" strokeWidth="1.5" />
        ))}
        {/* Right face (parallelogram) */}
        {[0, 1, 2].map((r) => (
          <polygon key={`r-${r}`}
            points={`${228},${110 + r * 38} ${248},${90 + r * 38} ${248},${128 + r * 38} ${228},${148 + r * 38}`}
            fill={faceColors[(r + 1) % 6]} stroke="#1e293b" strokeWidth="1.5" />
        ))}
      </g>
      <text x="200" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Rubik&apos;s Cube</text>
    </svg>
  );
};

// ── 7. Board Game ────────────────────────────────────────────────────
export const BoardGame: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  // Board path squares
  const squares = [
    { x: 100, y: 400 }, { x: 180, y: 400 }, { x: 260, y: 400 }, { x: 340, y: 400 },
    { x: 420, y: 400 }, { x: 500, y: 400 }, { x: 580, y: 400 }, { x: 660, y: 400 },
    { x: 660, y: 320 }, { x: 660, y: 240 }, { x: 660, y: 160 },
    { x: 580, y: 160 }, { x: 500, y: 160 }, { x: 420, y: 160 },
    { x: 340, y: 160 }, { x: 260, y: 160 }, { x: 180, y: 160 }, { x: 100, y: 160 },
  ];
  const sqColors = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x="80" y="130" width="620" height="300" rx="12" fill="#0f172a" stroke="#334155" strokeWidth="2" />
      {/* Draw path */}
      {squares.map((sq, i) => {
        const reached = i <= activeIndex;
        return (
          <g key={i}>
            <rect x={sq.x} y={sq.y} width="55" height="55" rx="6" fill={reached ? sqColors[i % sqColors.length] : "#1e293b"} stroke="#475569" strokeWidth="1.5" />
            <text x={sq.x + 27} y={sq.y + 32} textAnchor="middle" fill={reached ? "#fff" : "#475569"} fontSize="12" fontWeight="bold">{i + 1}</text>
          </g>
        );
      })}
      {/* Player token */}
      {activeIndex < squares.length && (
        <circle cx={squares[activeIndex].x + 27} cy={squares[activeIndex].y + 27} r="14" fill="#fbbf24" stroke="#f59e0b" strokeWidth="3">
          <animate attributeName="r" values="12;16;12" dur="1s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Start/Finish labels */}
      <text x="127" y="470" textAnchor="middle" fill="#22c55e" fontSize="10" fontWeight="bold">START</text>
      <text x="127" y="150" textAnchor="middle" fill="#ef4444" fontSize="10" fontWeight="bold">FINISH</text>
    </svg>
  );
};

// ── 8. Word Search ───────────────────────────────────────────────────
export const WordSearch: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const grid = [
    "LEARNENGIN",
    "XTHINKQRZE",
    "APUZZLEYWD",
    "MBRAINGFHU",
    "PIOUCSQHTC",
    "LKNOWQTSRA",
    "EREADMPVBT",
    "RSTUDYWQIE",
  ];
  const found = [
    { word: "LEARN", row: 0, col: 0, len: 5, dir: "h" },
    { word: "THINK", row: 1, col: 1, len: 5, dir: "h" },
    { word: "PUZZLE", row: 2, col: 1, len: 6, dir: "h" },
    { word: "BRAIN", row: 3, col: 1, len: 5, dir: "h" },
    { word: "READ", row: 6, col: 1, len: 4, dir: "h" },
    { word: "STUDY", row: 7, col: 1, len: 5, dir: "h" },
  ];
  const cellSize = 32;
  const ox = 160;
  const oy = 80;
  const highlighted = new Set<string>();
  found.slice(0, activeIndex + 1).forEach((w) => {
    for (let i = 0; i < w.len; i++) {
      highlighted.add(`${w.row},${w.col + i}`);
    }
  });
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x={ox - 5} y={oy - 5} width={10 * cellSize + 10} height={8 * cellSize + 10} rx="6" fill="#0f172a" stroke="#334155" strokeWidth="2" />
      {grid.map((row, r) =>
        row.split("").map((ch, c) => {
          const isHl = highlighted.has(`${r},${c}`);
          return (
            <g key={`${r}-${c}`}>
              <rect x={ox + c * cellSize} y={oy + r * cellSize} width={cellSize} height={cellSize} fill={isHl ? "#6366f1" : "#1e293b"} stroke="#334155" strokeWidth="0.5" rx="2" />
              <text x={ox + c * cellSize + cellSize / 2} y={oy + r * cellSize + cellSize / 2 + 5} textAnchor="middle" fill={isHl ? "#fff" : "#94a3b8"} fontSize="14" fontWeight="bold">{ch}</text>
            </g>
          );
        })
      )}
      {/* Word list */}
      <text x="600" y="110" fill="#e0e7ff" fontSize="14" fontWeight="bold">Find:</text>
      {found.map((w, i) => (
        <text key={i} x="600" y={140 + i * 25} fill={i <= activeIndex ? "#22c55e" : "#475569"} fontSize="12" textDecoration={i <= activeIndex ? "line-through" : "none"}>
          {w.word} {i <= activeIndex ? "✓" : ""}
        </text>
      ))}
    </svg>
  );
};
